import { WORKFLOWS } from './workflows'

/*
 * Tour definitions.
 *
 * Tours live here, in code, on purpose: they are written by whoever builds the
 * feature, in the same change that builds it. The trade-off accepted is that a
 * new tour needs a release — in exchange, the person writing the copy is the
 * one who knows what the control does.
 *
 * Two rules keep that honest, both enforced by
 * `yarn tours:check` (which also runs on every build):
 *   - a step pointing at an anchor that no longer exists fails the build;
 *   - an anchor with no tour pointing at it fails the build, unless it is
 *     listed with a reason in anchors-without-tours.json.
 *
 * Deliberately absent: per-role configuration. Targeting comes from whether a
 * step's anchor is in the DOM (see anchors.js) — if the user cannot see the
 * control, they do not get told about it.
 */

export const CTA = {
  next: 'next',
  dismiss: 'dismiss',
  clickAnchor: 'click_anchor'
}

export const HINTS = [
  {
    key: 'units-public-data',
    version: 1,
    // The same panel is mounted under three parents; a draft or a saved
    // passport shows the identical Sync Units button.
    routes: [
      '/bp-list/:id/units',
      '/drafts/:id/units',
      '/favourites/:id/units'
    ],
    trigger: 'auto_once',
    steps: [
      {
        anchor: 'units.sync-button',
        // Map-first passports keep this button disabled until the postcode
        // lookup resolves its UPRNs, which is well past the default window.
        waitMs: 20000,
        title: 'Sync units from the official address data',
        body:
          'Pull the unit list straight from the OS address register instead of typing every apartment in by hand.',
        placement: 'bottom',
        cta: { type: CTA.clickAnchor, label: 'Show me' }
      },
      {
        anchor: 'units.use-public-data',
        // Appears once the reconciliation dialog has fetched its candidates.
        waitMs: 15000,
        title: 'Prefer the official list?',
        body:
          'This rebuilds your units from public data in one go: matched units keep their files and people, other official addresses become new units, and saved units with no property reference are deleted.',
        placement: 'top',
        cta: { type: CTA.next, label: 'Got it' }
      }
    ]
  },
  {
    key: 'createbp-public-data-names',
    version: 1,
    routes: ['/create-bp'],
    trigger: 'auto_once',
    // A single-step callout is a first-class tour, not a degenerate one — it is
    // what most feature announcements actually need.
    steps: [
      {
        anchor: 'createBp.use-public-data-names',
        title: 'Name units the way the register does',
        body:
          'Tick this and every unit sync uses the apartment names from the official OS address data instead of the default ones. It is only a setting — it runs no address lookups now.',
        placement: 'top',
        cta: { type: CTA.next, label: 'Got it' }
      }
    ]
  },
  {
    key: 'site-risks',
    version: 1,
    // The menu is the same on a saved, draft or favourite passport.
    routes: [
      '/bp-list/:id/:folder',
      '/drafts/:id/:folder',
      '/favourites/:id/:folder'
    ],
    trigger: 'auto_once',
    steps: [
      {
        anchor: 'folders.site-risks',
        title: 'New: Site risks',
        body:
          'See which flood, heritage, planning and environmental designations apply to this building, screened against the GOV.UK planning data platform by UPRN.',
        placement: 'right',
        cta: { type: CTA.clickAnchor, label: 'Show me' }
      },
      {
        anchor: 'planning.summary',
        // The page fetches the screening result; a first check can still be
        // queued, in which case the summary only appears once it lands.
        waitMs: 20000,
        title: 'Flagged, clear or not recorded',
        body:
          'Red and orange tiles apply to this building. Green means clear. Grey means the council has not published that dataset yet, so it is not proof of absence. Open a tile for the record and links.',
        placement: 'bottom',
        cta: { type: CTA.next, label: 'Next' }
      },
      {
        anchor: 'planning.map-toggle',
        title: 'See it on the map',
        body:
          'Switch to the map to see the designations around the building on an OS base map, with a filter for every dataset and details on click.',
        placement: 'bottom',
        cta: { type: CTA.next, label: 'Got it' }
      }
    ]
  }

  // tours:new inserts here — keep this comment as the last line of the array
]

export const TOURS = [...HINTS, ...WORKFLOWS]

export const getTourByKey = key => HINTS.find(tour => tour.key === key) || null

export const getWorkflowByKey = key => WORKFLOWS.find(workflow => workflow.key === key) || null

/*
 * `/bp-list/:id/units` against `/bp-list/8f3.../units`. Hand-rolled rather than
 * react-router's matchPath so a malformed pattern degrades to "never matches"
 * instead of throwing inside a render.
 */
export const matchesRoute = (pattern, pathname) => {
  const source = String(pattern)
    .split('/')
    .filter(Boolean)
    .map(segment => {
      if (segment.startsWith(':')) {
        return segment.endsWith('?') ? '(?:/[^/]+)?' : '/[^/]+'
      }
      return '/' + segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    })
    .join('')

  try {
    return new RegExp(`^${source}/?$`).test(pathname)
  } catch (e) {
    return false
  }
}

export const findTourForRoute = pathname =>
  TOURS.find(
    tour =>
      tour.trigger === 'auto_once' &&
      (tour.routes || []).some(route => matchesRoute(route, pathname))
  ) || null
